import React, { useState, useRef, lazy } from 'react';
import { ChevronLeft, ChevronRight, BookOpenIcon, ClipboardListIcon, MonitorPlayIcon, HeartHandshakeIcon, ImageIcon, ArrowRightIcon, FileTextIcon } from 'lucide-react';
const items = [{
  number: '01',
  icon: BookOpenIcon,
  tag: '8 Volumi PDF',
  title: 'Il Percorso Completo',
  desc: 'Vol 1-3 (Medie): Aritmetica, Geometria, Algebra base, Esame di Stato. Vol 4-8 (Superiori): Algebra avanzata, Trigonometria, Analisi, Integrali e Maturità.',
  accent: 'cta-green'
}, {
  number: '02',
  icon: ClipboardListIcon,
  tag: 'Conforme MIUR',
  title: 'Registro del Programma',
  desc: "La soluzione definitiva alla burocrazia: Checklist Curriculum MIUR per monitorare l'avanzamento in 5 minuti.",
  accent: 'primary'
}, {
  number: '03',
  icon: MonitorPlayIcon,
  tag: 'Pronte per LIM',
  title: 'Slide Matematica Visuale',
  desc: 'Lezioni interattive che trasformano teoremi e funzioni in immagini logiche immediate, perfette per la lavagna interattiva.',
  accent: 'secondary'
}, {
  number: '04',
  icon: HeartHandshakeIcon,
  tag: 'BES & DSA',
  title: 'Recupero & Inclusione',
  desc: 'Schede di recupero rapido pre-verifica e strumenti semplificati per non lasciare indietro nessuno studente.',
  accent: 'cta-green'
}];
const accentMap: Record<string, {
  bg: string;
  text: string;
  border: string;
  bar: string;
}> = {
  'cta-green': {
    bg: 'bg-cta-green/10',
    text: 'text-cta-green',
    border: 'border-cta-green/30',
    bar: 'bg-cta-green'
  },
  primary: {
    bg: 'bg-primary/10',
    text: 'text-primary',
    border: 'border-primary/30',
    bar: 'bg-primary'
  },
  secondary: {
    bg: 'bg-secondary/10',
    text: 'text-secondary',
    border: 'border-secondary/30',
    bar: 'bg-secondary'
  }
};
const carouselImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/aVfkXi1yDMyNJVoQtPZiD8/Captura-de-pantalla-2026-05-07-a-las-7.19.42_p.-m..webp',
  alt: 'Attività 1.2 — Le Quattro Operazioni',
  category: 'Aritmetica',
  volume: 'Vol. 1 — Medie'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/my3xvEAPZzBNWd18LB3daX/Captura-de-pantalla-2026-05-07-a-las-7.20.03_p.-m..webp',
  alt: 'Operazioni con i Monomi',
  category: 'Algebra',
  volume: 'Vol. 3 — Medie'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/rNAifCEoupB2Bc2Z4Up1n8/Captura-de-pantalla-2026-05-07-a-las-7.20.19_p.-m..webp',
  alt: 'Problema Reale: La Piscina',
  category: 'Geometria applicata',
  volume: 'Vol. 4 — Superiori'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/6kv2GbvcSZFMM6zzm2s3nN/Captura-de-pantalla-2026-05-07-a-las-7.20.42_p.-m..webp',
  alt: 'Parabola Aperta in Su',
  category: 'Funzioni',
  volume: 'Vol. 6 — Superiori'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/dGFeMbmaQRjifrAig7TTFH/Captura-de-pantalla-2026-05-07-a-las-7.21.22_p.-m..webp',
  alt: 'Scelta Multipla — Analisi',
  category: 'Analisi',
  volume: 'Vol. 8 — Maturità'
}];
export function ContentPreview() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const rafRef = useRef<number>(0);
  const handleScroll = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      if (!scrollRef.current) return;
      const container = scrollRef.current;
      const scrollLeft = container.scrollLeft;
      const clientWidth = container.clientWidth;
      const center = scrollLeft + clientWidth / 2;
      const children = container.children;
      let minDistance = Infinity;
      let active = 0;
      for (let i = 0; i < children.length; i++) {
        const child = children[i] as HTMLElement;
        const childCenter = child.offsetLeft + child.offsetWidth / 2;
        const distance = Math.abs(center - childCenter);
        if (distance < minDistance) {
          minDistance = distance;
          active = i;
        }
      }
      setActiveIndex(active);
    });
  };
  const scrollToIndex = (index: number) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const child = container.children[index] as HTMLElement;
    if (!child) return;
    const scrollLeft = child.offsetLeft - container.clientWidth / 2 + child.clientWidth / 2;
    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  };
  const scrollPrev = () => {
    if (activeIndex > 0) scrollToIndex(activeIndex - 1);
  };
  const scrollNext = () => {
    if (activeIndex < carouselImages.length - 1) scrollToIndex(activeIndex + 1);
  };
  return <section className="w-full bg-bg-dark py-16 sm:py-24 px-4 relative overflow-hidden" data-id="element-67">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full pointer-events-none" style={{
      background: 'radial-gradient(circle, rgba(16,185,129,0.06) 0%, transparent 70%)'
    }} data-id="element-68"></div>

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-69">
        <div className="text-center mb-12 sm:mb-16" data-id="element-70">
          <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-cta-green uppercase tracking-[0.2em] mb-3 sm:mb-4 bg-cta-green/10 border border-cta-green/20 px-4 py-1.5 rounded-full" data-id="element-71">
            <FileTextIcon className="w-4 h-4" strokeWidth={2.5} data-id="element-72" />
            Archivio didattico
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main tracking-tight leading-tight max-w-3xl mx-auto" data-id="element-73">
            Cosa include
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cta-green to-emerald-300" data-id="element-74">
              {' '}
              Matematica Pronta
            </span>
          </h2>
          <p className="text-text-sec text-base sm:text-xl mt-4 max-w-2xl mx-auto leading-relaxed" data-id="element-75">
            Strumenti digitali in PDF pronti da stampare o proiettare da oggi
            stesso.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6" data-id="element-76">
          {items.map((item, i) => {
          const a = accentMap[item.accent];
          const Icon = item.icon;
          return <div key={i} className={`bg-bg-alt rounded-2xl border border-border-light overflow-hidden flex flex-col relative group hover:-translate-y-1 transition-all duration-300 hover:border-white/20`} data-id="element-77">
                <div className={`${a.bar} h-1 w-full`} data-id="element-78" />
                <div className="flex-1 flex flex-col p-6 sm:p-7 relative" data-id="element-79">
                  <div className="flex items-start justify-between gap-3 mb-5" data-id="element-80">
                    <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${a.bg} border ${a.border} flex items-center justify-center flex-shrink-0`} data-id="element-81">
                      <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${a.text}`} strokeWidth={2.2} data-id="element-82" />
                    </div>
                    <span className={`text-2xl sm:text-3xl font-black ${a.text} opacity-30 leading-none`} data-id="element-83">
                      {item.number}
                    </span>
                  </div>

                  <span className={`inline-flex items-center self-start text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${a.bg} ${a.text} border ${a.border} mb-3`} data-id="element-84">
                    {item.tag}
                  </span>

                  <h3 className="text-lg sm:text-2xl font-black text-text-main leading-tight mb-2.5" data-id="element-85">
                    {item.title}
                  </h3>
                  <p className="text-text-sec text-sm sm:text-base leading-relaxed" data-id="element-86">
                    {item.desc}
                  </p>
                </div>
              </div>;
        })}
        </div>

        <div className="mt-16 sm:mt-24 relative" data-id="element-87">
          <div className="text-center mb-8 sm:mb-10" data-id="element-88">
            <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-primary uppercase tracking-[0.2em] mb-3 bg-primary/10 border border-primary/20 px-4 py-1.5 rounded-full" data-id="element-89">
              <ImageIcon className="w-4 h-4" strokeWidth={2.5} data-id="element-90" />
              Anteprima reale
            </span>
            <h3 className="text-2xl sm:text-4xl font-black text-text-main tracking-tight leading-tight" data-id="element-91">
              Sfoglia il materiale all'interno
            </h3>
            <p className="text-text-sec text-sm sm:text-base mt-3 max-w-xl mx-auto" data-id="element-92">
              Schermate reali tratte dagli 8 volumi del Kit. Scorri per
              esplorare i contenuti.
            </p>
          </div>

          <div className="relative group max-w-[100vw] -mx-4 sm:mx-0" data-id="element-93">
            <button onClick={scrollPrev} className={`hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-bg-alt hover:bg-bg-main border border-border-light hover:border-cta-green/40 p-3 rounded-full text-text-main transition-all shadow-lg ${activeIndex === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Precedente" data-id="element-94">
              <ChevronLeft className="w-6 h-6" data-id="element-95" />
            </button>
            <button onClick={scrollNext} className={`hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-bg-alt hover:bg-bg-main border border-border-light hover:border-cta-green/40 p-3 rounded-full text-text-main transition-all shadow-lg ${activeIndex === carouselImages.length - 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Successivo" data-id="element-96">
              <ChevronRight className="w-6 h-6" data-id="element-97" />
            </button>

            <div ref={scrollRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 sm:gap-6 px-[10vw] sm:px-[calc(50%-175px)] pb-6 pt-4" data-id="element-98">
              {carouselImages.map((img, i) => <div key={i} className={`snap-center shrink-0 w-[80vw] sm:w-[350px] bg-bg-alt rounded-2xl overflow-hidden p-2 sm:p-3 flex items-center justify-center transition-all duration-300 relative ${activeIndex === i ? 'scale-100 opacity-100 border-2 border-cta-green/40 shadow-[0_10px_40px_rgba(16,185,129,0.25)]' : 'scale-95 opacity-50 border border-border-light shadow-[0_10px_30px_rgba(0,0,0,0.3)]'}`} data-id="element-99">
                  <span className={`absolute top-4 left-4 z-10 text-[10px] sm:text-xs font-black uppercase tracking-wider px-2.5 py-1 rounded-full backdrop-blur-md transition-opacity duration-300 ${activeIndex === i ? 'bg-cta-green/90 text-white shadow-[0_4px_12px_rgba(16,185,129,0.4)] opacity-100' : 'bg-black/60 text-white/80 opacity-90'}`} data-id="element-100">
                    {img.category}
                  </span>
                  <img src={img.src} alt={img.alt} loading="lazy" decoding="async" width={800} height={1131} className="w-full h-auto max-h-[400px] sm:max-h-[450px] object-contain rounded-xl bg-white/5" data-id="element-101" />
                </div>)}
            </div>
          </div>

          {/* Active slide caption */}
          <div className="max-w-2xl mx-auto px-4 mt-2 mb-6" data-id="element-102">
            <div key={activeIndex} className="bg-bg-alt border border-border-light rounded-xl px-4 sm:px-5 py-3 sm:py-4 flex items-center gap-3 sm:gap-4 animate-[fadeIn_0.3s_ease]" data-id="element-103">
              <span className="flex-shrink-0 bg-cta-green/15 text-cta-green border border-cta-green/30 rounded-lg px-2.5 py-1 text-[10px] sm:text-xs font-black uppercase tracking-wider" data-id="element-104">
                {String(activeIndex + 1).padStart(2, '0')} /{' '}
                {String(carouselImages.length).padStart(2, '0')}
              </span>
              <div className="flex-1 min-w-0" data-id="element-105">
                <p className="text-text-main font-bold text-sm sm:text-base truncate" data-id="element-106">
                  {carouselImages[activeIndex].alt}
                </p>
                <p className="text-text-sec text-xs sm:text-sm font-medium" data-id="element-107">
                  {carouselImages[activeIndex].volume}
                </p>
              </div>
            </div>
          </div>

          {/* Thumbnail strip */}
          <div className="flex justify-center gap-2 sm:gap-3 px-4 mb-2 overflow-x-auto scrollbar-hide" data-id="element-108">
            {carouselImages.map((img, i) => <button key={i} onClick={() => scrollToIndex(i)} className={`relative shrink-0 rounded-lg overflow-hidden transition-all duration-300 ${i === activeIndex ? 'ring-2 ring-cta-green ring-offset-2 ring-offset-bg-dark scale-105 shadow-[0_0_15px_rgba(16,185,129,0.4)]' : 'ring-1 ring-border-light opacity-50 hover:opacity-80'}`} aria-label={`Vai a ${img.alt}`} data-id="element-109">
                <img src={img.src} alt="" loading="lazy" decoding="async" width={64} height={90} className="w-12 h-16 sm:w-14 sm:h-20 object-cover bg-white/5" data-id="element-110" />
              </button>)}
          </div>

          {/* Progress bar */}
          <div className="max-w-xs mx-auto h-1 bg-border-light rounded-full overflow-hidden mt-4" data-id="element-111">
            <div className="h-full bg-cta-green rounded-full transition-all duration-500 ease-out shadow-[0_0_8px_rgba(16,185,129,0.6)]" style={{
            width: `${(activeIndex + 1) / carouselImages.length * 100}%`
          }} data-id="element-112" />
          </div>

          <div className="mt-10 sm:mt-12 text-center" data-id="element-113">
            <a href="#pricing" className="inline-flex items-center gap-2 text-cta-green font-bold text-sm sm:text-base hover:gap-3 transition-all" data-id="element-114">
              Voglio tutto questo nel mio Kit
              <ArrowRightIcon className="w-4 h-4" strokeWidth={2.5} data-id="element-115" />
            </a>
          </div>
        </div>
      </div>
    </section>;
}